import PropTypes from "prop-types"
import { useContext } from "react"
import { useLocale, useT } from "react-polyglot-hooks"
import LazyLoad from "react-lazyload"
import { Container, Section } from "components/styled/Grid"
import dynamic from "next/dynamic"
import Breadcrumbs from "components/layouts/Breadcrumbs"
import styled from "styled-components"
import { color, hideAt, mq, showAt } from "helpers/styled"
import CarSeo from "components/SEO/CarSeo"
import Header from "scenes/CarScene/Header"
import Video from "scenes/CarScene/Video"
import Promotions from "scenes/CarScene/Promotions"
import Features from "./Features"
import Specs from "./Specs"
import { CarContext } from "./CarContext"

const DefaultLayout = dynamic(() => import("components/layouts/DefaultLayout"))
const MinimalLayout = dynamic(() => import("components/layouts/MinimalLayout"))
const Plans = dynamic(() => import("./Plans"), { ssr: false })
const Services = dynamic(() => import("./Services"))
const Faqs = dynamic(() => import("./FlexibleFaqs"))
const Extras = dynamic(() => import("./Extras"))
const Disclaimer = dynamic(() => import("components/car/Disclaimer"))
const Social = dynamic(() => import("./Social"))
const SideBar = dynamic(() => import("./SideBar"))
const Form = dynamic(() => import("./Form"))
const OtherCars = dynamic(() => import("./OtherCars"))
const DownPaymentDisclaimer = dynamic(() => import("components/DownPaymentDisclaimer"))
const InfoBanner = dynamic(() => import("components/InfoBanner"))

const Mobile = styled.div`
  ${hideAt("md")}
`

const Tablet = styled.div`
  ${hideAt("lg")}
`

const Desktop = styled.div`
  ${showAt("md")}
`

const Grid = styled.div`
  /* Used in <Plancontainer>'s styles */
  --grid-gap: 0;

  display: grid;
  grid-template-columns: 100%;
  gap: var(--grid-gap);

  @media ${mq("lg")} {
    --grid-gap: 25px;

    grid-template-columns: 540px 1fr;
  }

  @media ${mq("xl")} {
    --grid-gap: 50px;

    grid-template-columns: 720px 1fr;
  }
`

const Background = styled.div`
  margin-top: 50px;
  background-color: ${color("white")};

  ${Grid} > * {
    z-index: 1;
  }
`

export default function CarScene({ minimal }) {
  const { car, page, footerLinks } = useContext(CarContext)
  const t = useT()
  const locale = useLocale()

  const { model, brand, version, slug, sold, sellingStatus, brandImageUrlPath } = car

  const Layout = minimal ? MinimalLayout : DefaultLayout

  const slugParts = slug.split("/").filter(part => part !== 'compra-flexible');

  const flexible_page = sellingStatus === 'flexible' ? 'flexible_' : ''
  const prefix = sellingStatus === 'flexible' ? 'Compra Flexible' : 'Renting'

  const breadCrumbItems = [
    { label: `${prefix} ${t('common.menu.stock')}`, title: `${prefix} ${t('common.menu.stock')}`, page: `${flexible_page}stock` },
    { label: brand, title: `${prefix} ${brand}`, page: `${flexible_page}brand`, params: { brand: brandImageUrlPath } },
    {
      label: model,
      title: `${prefix} ${brand} ${model}`,
      page: `${flexible_page}model`,
      params: { brand: brandImageUrlPath, model: slugParts[1] }
    }
  ]

  return (
    <>
      <CarSeo hide={!!minimal || sellingStatus === 'pre-owned'} />

      <Layout page={page} {...minimal} footerLinks={footerLinks}>
        {!minimal && (
          <Breadcrumbs
            items={breadCrumbItems}
            current={version}
            currentTitle={`${prefix} ${brand} ${model} ${version}`}
            page={page}
          />
        )}
        <Background>
          <Container>
            <Grid>
              <div>
                <Header />
                <Features />
                <Specs />
              </div>
              <div
                css={`
                  position: relative;
                  ${showAt("lg")};
                `}>
                <SideBar />
              </div>
            </Grid>
          </Container>
        </Background>

        <Container>
          <Grid>
            <div>
              <Section top={25} bottom="0">
                <Promotions />
              </Section>

              <Section top={25}>
                <Desktop>
                  <Services />
                </Desktop>
                <Mobile>
                  <Extras />
                </Mobile>
              </Section>

              {locale === "es" && sellingStatus === "pre-owned" && (
                <Section top="0" bottom="0">
                  <Tablet>
                    <InfoBanner>
                      Solamente se reacondicionan los desperfectos mecánicos y elementos de seguridad
                    </InfoBanner>
                  </Tablet>
                </Section>
              )}

              {locale === "it" && (
                <Section top="0" bottom="0">
                  <Tablet>
                    <DownPaymentDisclaimer />
                  </Tablet>
                </Section>
              )}

              {!sold && <Plans />}

              <Section bottom={50}>
                <Desktop>
                  <Extras />
                </Desktop>
                <Mobile>
                  <Services />
                </Mobile>

                <Disclaimer />
                <Video />
                {sellingStatus !== 'flexible' && <Faqs />}
                <Social />
              </Section>
            </div>
          </Grid>
        </Container>
        <Form />
        {sellingStatus === 'flexible' && <Faqs />}

        {!minimal && (
          <LazyLoad height={500} once>
            <OtherCars />
          </LazyLoad>
        )}
      </Layout>
    </>
  )
}

CarScene.defaultProps = {
  minimal: null
}

CarScene.propTypes = {
  minimal: PropTypes.object
}
